import { useState } from 'react';
import { X, Plus, Trash2, Sparkles } from 'lucide-react';
import {
  useSchoolChecklist,
  useCreateSchoolChecklistItem,
  useDeleteSchoolChecklistItem,
  useCreateSchoolChecklistOverride,
  useDeleteSchoolChecklistOverride,
  useCreateSchoolKeywordRule,
  useDeleteSchoolKeywordRule,
  useFamilyMembers,
} from '../../api/hooks';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface SchoolChecklistSettingsProps {
  onClose: () => void;
}

function DayOfWeekPicker({
  value,
  onChange,
}: {
  value: number[];
  onChange: (days: number[]) => void;
}) {
  const toggle = (day: number) => {
    if (value.includes(day)) {
      onChange(value.filter((d) => d !== day));
    } else {
      onChange([...value, day].sort());
    }
  };

  return (
    <div className="flex gap-1">
      {DAY_LABELS.map((label, idx) => (
        <button
          key={idx}
          type="button"
          onClick={() => toggle(idx)}
          className={`w-8 h-8 rounded-lg text-xs font-semibold transition-all ${
            value.includes(idx)
              ? 'bg-teal text-white shadow-sm'
              : 'bg-bg-primary text-text-secondary hover:bg-bg-secondary'
          }`}
        >
          {label.charAt(0)}
        </button>
      ))}
    </div>
  );
}

export function SchoolChecklistSettings({ onClose }: SchoolChecklistSettingsProps) {
  const { data: checklistData } = useSchoolChecklist();
  const { data: membersData } = useFamilyMembers();
  const createItem = useCreateSchoolChecklistItem();
  const deleteItem = useDeleteSchoolChecklistItem();
  const createOverride = useCreateSchoolChecklistOverride();
  const deleteOverride = useDeleteSchoolChecklistOverride();
  const createKeywordRule = useCreateSchoolKeywordRule();
  const deleteKeywordRule = useDeleteSchoolKeywordRule();

  const childMembers = (membersData || []).filter(
    (m) => m.type === 'child' || m.type === 'teen'
  );

  // New base item form
  const [newItemTitle, setNewItemTitle] = useState('');
  const [newItemCategory, setNewItemCategory] = useState('');
  const [newItemDays, setNewItemDays] = useState<number[]>([1, 2, 3, 4, 5]); // Mon-Fri default

  // New override form
  const [newOverrideTitle, setNewOverrideTitle] = useState('');
  const [newOverrideMemberId, setNewOverrideMemberId] = useState(childMembers[0]?.id || '');
  const [newOverrideDays, setNewOverrideDays] = useState<number[]>([1, 2, 3, 4, 5]);

  // New keyword rule form
  const [newKeyword, setNewKeyword] = useState('');
  const [newKeywordItem, setNewKeywordItem] = useState('');

  const [activeTab, setActiveTab] = useState<'shared' | 'perchild' | 'keywords'>('shared');

  const handleAddBaseItem = () => {
    if (!newItemTitle.trim()) return;
    createItem.mutate({
      title: newItemTitle.trim(),
      category: newItemCategory.trim() || undefined,
      daysOfWeek: newItemDays,
    });
    setNewItemTitle('');
    setNewItemCategory('');
  };

  const handleAddOverride = () => {
    if (!newOverrideTitle.trim() || !newOverrideMemberId) return;
    createOverride.mutate({
      memberId: newOverrideMemberId,
      title: newOverrideTitle.trim(),
      daysOfWeek: newOverrideDays,
    });
    setNewOverrideTitle('');
  };

  const handleAddKeywordRule = () => {
    if (!newKeyword.trim() || !newKeywordItem.trim()) return;
    createKeywordRule.mutate({
      keyword: newKeyword.trim(),
      checklistItem: newKeywordItem.trim(),
    });
    setNewKeyword('');
    setNewKeywordItem('');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-bg-surface rounded-2xl shadow-elevated border border-border-default w-full max-w-lg max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-default">
          <h2 className="font-display text-lg font-semibold text-text-primary">
            School Checklist Settings
          </h2>
          <button onClick={onClose} className="btn-icon p-1.5">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border-default px-6">
          {([
            { key: 'shared', label: 'Shared Items' },
            { key: 'perchild', label: 'Per Child' },
            { key: 'keywords', label: 'Smart Rules' },
          ] as const).map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-teal text-teal'
                  : 'border-transparent text-text-secondary hover:text-text-primary'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto px-6 py-4">
          {activeTab === 'shared' && (
            <div className="space-y-4">
              <p className="text-xs text-text-secondary">
                Items shared across all children. Use the day picker to set which days each item appears.
              </p>

              {/* Existing items */}
              <div className="space-y-2">
                {(checklistData?.baseItems || []).map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-bg-primary"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-text-primary truncate">
                        {item.title}
                      </div>
                      <div className="text-xs text-text-secondary mt-0.5">
                        {item.daysOfWeek.length === 0
                          ? 'Every day'
                          : item.daysOfWeek.map((d) => DAY_LABELS[d]).join(', ')}
                        {item.category && ` · ${item.category}`}
                      </div>
                    </div>
                    <button
                      onClick={() => deleteItem.mutate(item.id)}
                      className="text-text-muted hover:text-coral transition-colors p-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Add new item form */}
              <div className="border border-border-default rounded-xl p-3 space-y-3">
                <input
                  type="text"
                  value={newItemTitle}
                  onChange={(e) => setNewItemTitle(e.target.value)}
                  placeholder="Item name (e.g., Lunch box)"
                  className="w-full px-3 py-2 rounded-lg border border-border-default text-sm focus:outline-none focus:ring-2 focus:ring-teal/30 focus:border-teal bg-bg-primary"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddBaseItem()}
                />
                <input
                  type="text"
                  value={newItemCategory}
                  onChange={(e) => setNewItemCategory(e.target.value)}
                  placeholder="Category (optional, e.g., Uniform)"
                  className="w-full px-3 py-2 rounded-lg border border-border-default text-sm focus:outline-none focus:ring-2 focus:ring-teal/30 focus:border-teal bg-bg-primary"
                />
                <div>
                  <div className="text-xs text-text-secondary mb-1.5">Days (empty = every day)</div>
                  <DayOfWeekPicker value={newItemDays} onChange={setNewItemDays} />
                </div>
                <button
                  onClick={handleAddBaseItem}
                  disabled={!newItemTitle.trim()}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-teal text-white text-sm font-semibold hover:bg-teal-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Plus className="w-4 h-4" />
                  Add Item
                </button>
              </div>
            </div>
          )}

          {activeTab === 'perchild' && (
            <div className="space-y-4">
              <p className="text-xs text-text-secondary">
                Items specific to individual children (in addition to shared items).
              </p>

              {/* Existing overrides grouped by member */}
              {childMembers.map((member) => {
                const memberOverrides = (checklistData?.overrides || []).filter(
                  (o) => o.memberId === member.id
                );
                if (memberOverrides.length === 0) return null;
                return (
                  <div key={member.id}>
                    <div className="flex items-center gap-2 mb-2">
                      <div
                        className="w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-bold text-white"
                        style={{ backgroundColor: member.color }}
                      >
                        {member.name.charAt(0)}
                      </div>
                      <span className="text-xs font-semibold text-text-secondary">
                        {member.name}
                      </span>
                    </div>
                    <div className="space-y-2 ml-7">
                      {memberOverrides.map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center gap-3 px-3 py-2 rounded-xl bg-bg-primary"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-text-primary truncate">
                              {item.title}
                            </div>
                            <div className="text-xs text-text-secondary">
                              {item.daysOfWeek.length === 0
                                ? 'Every day'
                                : item.daysOfWeek.map((d) => DAY_LABELS[d]).join(', ')}
                            </div>
                          </div>
                          <button
                            onClick={() => deleteOverride.mutate(item.id)}
                            className="text-text-muted hover:text-coral transition-colors p-1"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {/* Add new override form */}
              <div className="border border-border-default rounded-xl p-3 space-y-3">
                {childMembers.length > 1 && (
                  <select
                    value={newOverrideMemberId}
                    onChange={(e) => setNewOverrideMemberId(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-border-default text-sm focus:outline-none focus:ring-2 focus:ring-teal/30 focus:border-teal bg-bg-primary"
                  >
                    {childMembers.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                )}
                <input
                  type="text"
                  value={newOverrideTitle}
                  onChange={(e) => setNewOverrideTitle(e.target.value)}
                  placeholder="Item name (e.g., Violin)"
                  className="w-full px-3 py-2 rounded-lg border border-border-default text-sm focus:outline-none focus:ring-2 focus:ring-teal/30 focus:border-teal bg-bg-primary"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddOverride()}
                />
                <div>
                  <div className="text-xs text-text-secondary mb-1.5">Days</div>
                  <DayOfWeekPicker value={newOverrideDays} onChange={setNewOverrideDays} />
                </div>
                <button
                  onClick={handleAddOverride}
                  disabled={!newOverrideTitle.trim() || !newOverrideMemberId}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-teal text-white text-sm font-semibold hover:bg-teal-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Plus className="w-4 h-4" />
                  Add Item for {childMembers.find((m) => m.id === newOverrideMemberId)?.name || 'Child'}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'keywords' && (
            <div className="space-y-4">
              <p className="text-xs text-text-secondary">
                Auto-detect items from calendar events. When an event title contains the keyword,
                the checklist item appears automatically.
              </p>

              {/* Existing rules */}
              <div className="space-y-2">
                {(checklistData?.keywordRules || []).map((rule) => (
                  <div
                    key={rule.id}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-bg-primary"
                  >
                    <Sparkles className="w-4 h-4 text-teal flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-text-primary truncate">
                        "{rule.keyword}" → {rule.checklistItem}
                      </div>
                    </div>
                    <button
                      onClick={() => deleteKeywordRule.mutate(rule.id)}
                      className="text-text-muted hover:text-coral transition-colors p-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Add new rule form */}
              <div className="border border-border-default rounded-xl p-3 space-y-3">
                <input
                  type="text"
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  placeholder="Keyword (e.g., swimming)"
                  className="w-full px-3 py-2 rounded-lg border border-border-default text-sm focus:outline-none focus:ring-2 focus:ring-teal/30 focus:border-teal bg-bg-primary"
                />
                <input
                  type="text"
                  value={newKeywordItem}
                  onChange={(e) => setNewKeywordItem(e.target.value)}
                  placeholder="Checklist item (e.g., Swim gear + towel)"
                  className="w-full px-3 py-2 rounded-lg border border-border-default text-sm focus:outline-none focus:ring-2 focus:ring-teal/30 focus:border-teal bg-bg-primary"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddKeywordRule()}
                />
                <button
                  onClick={handleAddKeywordRule}
                  disabled={!newKeyword.trim() || !newKeywordItem.trim()}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-teal text-white text-sm font-semibold hover:bg-teal-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Plus className="w-4 h-4" />
                  Add Smart Rule
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
